import React from "react";
// ********************************************************************************
// ****  SCORING ALGORITHMS  ***** there are three exported fns:
// getScoreText
// getAbilityStatusText
// extractAbilities
// ********************************************************************************
const EXCELLENT = 0.9;
const NEEDS_IMPROVEMENT = 0.5;

// TODO: change this any to unknown and use guards to narrow the type safely
const getAbilityPoints = (abilityId: string, abilities: any[]) => {
  const currentAbility = abilities.find(ability => ability.ability_id === abilityId);

  return currentAbility.score * currentAbility.weight * 100;
};

const getAbilityWeight = (abilityId: string, abilities: any[]) => {
  const currentAbility = abilities.find(ability => ability.ability_id === abilityId);

  return currentAbility.weight * 100;
};

const GRADED_ABILITIES = ["Themes", "Talking Points", "Key Phrases"];
const GRADED_ABILITY_IDS = ["SEMANTIC_SIMILARITY", "FOCAL_POINT", "KEY_PHRASE"];

const getAbilityMatchScore = (abilityId: string, analyses: any[]) => {
  // console.info("IFX: getAbilityMatchScore called for abilityId:", abilityId); // DEBUG
  // console.info ("from: analyses:", analyses); // DEBUG
  const currentAbility = analyses.find(analyses => analyses.ability_id === abilityId);
  // console.info("IFX: got currentAbility:", currentAbility); // DEBUG
  let stringMatchesFound = 0;
  if (GRADED_ABILITY_IDS.indexOf(abilityId) < 0) {
    // console.info("ungradeded ability match score is:", currentAbility.results.score > 0 ? 1 : 0); // DEBUG
    return currentAbility.results.score > 0 ? 1 : 0;
  } else {
    // the trick here is if there is a difference between the number found and the amount of positive feedback
    // if they match, there is no "You said something you should not have said" feedback
    // if they do not match, then there is a "You said something you should not
    switch (abilityId) {
      case "SEMANTIC_SIMILARITY":
        stringMatchesFound = currentAbility.results.matches.length;
        // console.info("Found ", stringMatchesFound, " Theme matches"); // DEBUG
        break;
      case "FOCAL_POINT":
        stringMatchesFound = currentAbility.results.found.length;
        // console.info("Found ", stringMatchesFound, " Talking Points matches"); // DEBUG
        break;
      case "KEY_PHRASE":
        stringMatchesFound = currentAbility.results.phrases_found.length;
        // console.info("Found ", stringMatchesFound, " Key Phrase matches"); // DEBUG
        break;
      default:
        console.warn("IFX: Unknown abilityId in getAbilityMatchScore:", abilityId);
        break; // Default case if abilityId is not recognized
    }
    // console.info("stringMatchesFound:", stringMatchesFound); // DEBUG
    const positiveFeedbackFound = currentAbility.results.feedback.length;
    // console.info("positiveFeedbackFound:", positiveFeedbackFound); // DEBUG
    const dontsayitFound = stringMatchesFound - positiveFeedbackFound;
    const matchScore = Math.max(stringMatchesFound - dontsayitFound, 0);
    // console.info("IFX: Graded MatchScore is:", matchScore); // DEBUG
    return matchScore;
  }
};

const getAbilityPassed = (
  abilityTitle: string,
  points: number,
  passed_graded: number,
) => {
  if (GRADED_ABILITIES.indexOf(abilityTitle) < 0) {
    return points > 0;
  } else {
    return passed_graded === 1;
  }
};

interface AbilityFeedback {
  feedback: string;
  isPositive: boolean;
}

// Scoring Logic from the mobile app.  THIS IS THE MOST CONVOLUTED LOGIC IN THE ENTIRE DASHBOARD.  Sheee-ite.
// from the database to the json, through the logic in the middleware, to the user interface, it's a mess.
// the old code logic was that zero points meant "you failed" and any positive points meant "you passed"
// but the new logic is that you can pass with scores above 0 based on the number of required points, 
// and you can fail with a score above 0 if you did not meet the required points. 
const getAbilityFeedback = (
  name: string,
  passed: boolean,
  feedback?: string[] | string,
  feedbackExtra?: string[] | string
): AbilityFeedback[] => {
  const finalFeedbackList: AbilityFeedback[] = [];

  let feedbackList: string[] = [];
  if (feedback) {
    feedbackList = typeof feedback === "string" ? [feedback] : feedback;
  }

  let extraFeedbackList: string[] = [];
  if (feedbackExtra) {
    extraFeedbackList = typeof feedbackExtra === "string" ? [feedbackExtra] : feedbackExtra;
  }

  if (GRADED_ABILITIES.indexOf(name) < 0) {
    // simple abilities; they pass or fail.  "feedback" is positive, "feedback_extra" is negative
    feedbackList.forEach(f => {
      finalFeedbackList.push({
        feedback: f,
        isPositive: passed,
      });
    });
    // OTHERWISE, GRADED ABILITIES have a more convoluted logic:
    // if they have passed the ability, then the feedback is positive, feedback_extra is negative
    // BUT IF THEY HAVE NOT PASSED, then the feedback is negative, feedback_extra is positive
    // so first, we determine if they passed or not:
  } else if (passed && feedbackList.length > 0 && extraFeedbackList.length > 0) {
    // they passed, but with some negatvie feedback, so feedback is positive, feedback_extra is negative
    feedbackList.forEach(f => {
      finalFeedbackList.push({
        feedback: f,
        isPositive: true,
      });
    });
    extraFeedbackList.forEach(f => {
      finalFeedbackList.push({
        feedback: f,
        isPositive: false,
      });
    });
  } else if (!passed && feedbackList.length > 0 && extraFeedbackList.length > 0) {
    // in this case, they did not pass, but some feedback was positive, so feedback is negative, feedback_extra is positive
    // therefore we reverse the logic:
    extraFeedbackList.forEach(f => {
      finalFeedbackList.push({
        feedback: f,
        isPositive: true,
      });
    });
    feedbackList.forEach(f => {
      finalFeedbackList.push({
        feedback: f,
        isPositive: false,
      });
    });
  } else {
    // there is no feedback_extra, so we just use the feedback and assume it's all positive or all negative.
    feedbackList.forEach(f => {
      finalFeedbackList.push({
        feedback: f,
        isPositive: passed,
      });
    });
  }

  return finalFeedbackList;
};

export const extractAbilities = (analysisResponse: {
  analyses: any[];
  // TODO: fix types
  skills: { current: { abilities: any[] } };
}) =>
  analysisResponse.analyses.map(analysis => {
    const abilityPoints = getAbilityPoints(
      analysis.ability_id,
      analysisResponse.skills.current.abilities
    );

    const abilityWeight = getAbilityWeight(
      analysis.ability_id,
      analysisResponse.skills.current.abilities
    );

    const abilityNumberRight = getAbilityMatchScore(
      analysis.ability_id,
      analysisResponse.analyses
    );

    const abilityPassed = getAbilityPassed(
      analysis.title,
      abilityPoints,
      analysis.results.passed_graded,
    );

    const abilityFeedback = getAbilityFeedback(
      analysis.title,
      abilityPassed,
      analysis.results.feedback,
      analysis.results.feedback_extra
    );

    return {
      name: analysis.app_title,
      description: analysis.app_description,
      priority: analysis.priority,
      numRequired: analysis.results.required ? analysis.results.required : 0,
      abilityNumberRight: abilityNumberRight,
      abilityPassed: abilityPassed,
      points: abilityPoints,
      feedback: abilityFeedback,
      possiblePoints: abilityWeight,
    };
  });

export const getScoreText = (passed: boolean, score: number) => {
  if (!passed) { // they did NOT pass
    if (score < NEEDS_IMPROVEMENT) { // and the score is low
      return (
        <h5 className="text-danger" style={{ textAlign: "center" }}>
          Needs improvement.
        </h5>
      );
    } else { // they did NOT pass but they had a good score
      return (
        <h5 className="text-warning" style={{ textAlign: "center" }}>
          Almost there...
        </h5>
      );
    }
  } else { // they passed 
    if (score > EXCELLENT) { // and the score is high
      return (
        <h5 className="text-success" style={{ textAlign: "center" }}>
          Excellent!
        </h5>
      );
    } else { // they passed but not with a great score
      return (
        <h5 className="text-success" style={{ textAlign: "center" }}>
          You Passed - Well Done.
        </h5>
      );
    }
  }
};


export function getAbilityStatusText(
  title: string,
  priority: number,
  numTotal: number,
  numRequired: number,
  passed: boolean,
  maxRequiredPriority = 2,
) {
  if (priority > maxRequiredPriority) { // It's an extra credit ability
    if (passed) return <p><i>Extra credit earned!</i></p>;
    else return <p><i>You could improve your score with extra credit.</i></p>;
  }
  // It's a required ability, so we first check to see if it has multiple possible points("graded ability")
  if (GRADED_ABILITIES.indexOf(title) < 0) { // Not a graded ability
    // it's just a regular ability, so we check for the basic pass/fail status
    if (passed) return <p><i>Well Done!</i></p>;
    else return <p><i>You need to get this right to pass.</i></p>;    // "Passed" or "Try Again" 
  }

    // IF we get here, It's a graded ability, 
  if (passed) {
    // Did they nail it? 
    if (numRequired && (numTotal === numRequired)) {
      return <p><i>Well Done!</i></p>;
    } else {
      return <p><i>You covered enough to pass.</i></p>;
    }
  }
  else {  // last case: a multi-ability that they failed to pass:
  //   // Difficulty arises when they said something they should not have said, so we need to tell them that
  //   if (numRequired && numTotal > numRequired) {
  //         return ( 
  //   <p><i>
  //     Try again. You can't miss more than {numRequired ? numTotal - numRequired : "?"}
  //   </i></p>
  //         )
  //   } else {
  //         return ( 
  //   <p><i>
  //     Try again. You can't miss more than {numRequired ? numTotal - numRequired : "?"}
  //   </i></p>
  //   )
  // }
    // but for now, since we don't have the data needed for specific feedback, we just say:
    return ( 
    <p><i>
      Try again. Some tips to improve your response: 
    </i></p>
  );
}
}
// ************************************************************************************************
